import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Truck, CheckCircle, Wallet, ArrowRight, Clock } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import api from "@/utils/api";
import useCustomerAmounts, { amountText } from "@/hooks/useCustomerAmounts";

export default function CustomerDashboard() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const amounts = useCustomerAmounts((data?.recent_shipments || []).map((s) => s.id));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get("/customer/dashboard");
      setData(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: "Total Shipments", value: data?.total_shipments || 0, icon: Package, color: "text-brand-500", bg: "bg-brand-50" },
    { label: "Active Shipments", value: data?.active || 0, icon: Truck, color: "text-orange-500", bg: "bg-orange-50" },
    { label: "Delivered", value: data?.delivered || 0, icon: CheckCircle, color: "text-emerald-500", bg: "bg-emerald-50" },
    { label: "Wallet Balance", value: `₹${(data?.wallet_balance || user?.wallet_balance || 0).toFixed(2)}`, icon: Wallet, color: "text-purple-500", bg: "bg-purple-50" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Welcome */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">
              Welcome back, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">Here's an overview of your shipments.</p>
          </div>
          <Link
            to="/customer/book"
            data-testid="quick-book-btn"
            className="hidden sm:flex items-center gap-2 px-5 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 transition-colors uppercase"
          >
            <Package className="w-4 h-4" /> Book Shipment
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, "-")}`}
              className="bg-white rounded-lg p-4 border border-slate-200 border-l-4 border-l-brand-500"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
                <div className={`w-8 h-8 ${stat.bg} rounded-md flex items-center justify-center`}>
                  <stat.icon className={`w-4 h-4 ${stat.color}`} strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-heading font-bold text-slate-800">{loading ? "—" : stat.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Recent Shipments */}
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="flex items-center justify-between p-4 border-b border-slate-100">
            <h2 className="font-heading font-semibold text-lg text-slate-800 uppercase flex items-center gap-2">
              <Clock className="w-5 h-5 text-brand-500" /> Recent Shipments
            </h2>
            <Link to="/customer/shipments" className="text-sm text-brand-500 font-semibold hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading...</div>
          ) : data?.recent_shipments?.length > 0 ? (
            <div className="divide-y divide-slate-50">
              {data.recent_shipments.map((s) => (
                <div key={s.id} data-testid="shipment-row" className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-9 h-9 bg-brand-50 rounded-md flex items-center justify-center flex-shrink-0">
                      <Package className="w-4 h-4 text-brand-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-slate-800 font-heading">{s.tracking_id}</p>
                      <p className="text-xs text-slate-400">{s.origin?.city} → {s.destination?.city}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={s.status} />
                    <p className="text-xs text-slate-400 mt-1">{amountText(amounts[s.id])}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No shipments yet.</p>
              <Link to="/customer/book" className="text-brand-500 text-sm font-semibold hover:underline mt-2 inline-block">Book your first shipment →</Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { to: "/customer/book", label: "Book New Shipment", icon: Package, desc: "Create a new shipment booking" },
            { to: "/customer/wallet", label: "Add Wallet Funds", icon: Wallet, desc: "Recharge your wallet balance" },
            { to: "/track", label: "Track Shipment", icon: Truck, desc: "Track any shipment by ID" },
          ].map((action) => (
            <Link
              key={action.to}
              to={action.to}
              data-testid={`quick-action-${action.label.toLowerCase().replace(/ /g, "-")}`}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:border-brand-500 hover:shadow-sm transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-brand-50 rounded-lg flex items-center justify-center group-hover:bg-brand-500 transition-colors">
                <action.icon className="w-5 h-5 text-brand-500 group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-800">{action.label}</p>
                <p className="text-xs text-slate-400">{action.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
